import Trix from "trix";

class BaseElement extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }
}

function innerHTML(alignment) {
  return `
    <style>
      :host {
        text-align: ${alignment};
        width: 100%;
        display: block;
      }
    </style>

    <slot></slot>
  `;
}
export class AlignCenterElement extends BaseElement {
  constructor() {
    super();

    this.shadowRoot.innerHTML = innerHTML("center");
  }
}

export class AlignRightElement extends BaseElement {
  constructor() {
    super();

    this.shadowRoot.innerHTML = innerHTML("right");
  }
}

export class AlignLeftElement extends BaseElement {
  constructor() {
    super();

    this.shadowRoot.innerHTML = innerHTML("left");
  }
}

window.customElements.define("align-left", AlignLeftElement);
window.customElements.define("align-center", AlignCenterElement);
window.customElements.define("align-right", AlignRightElement);

document.addEventListener("trix-before-initialize", () => {
  // Reduce undo consolidation window from 5s to 1s so each undo step
  // captures less work and feels more granular.
  Trix.config.undo.interval = 1000;

  Trix.config.textAttributes.underline = {
    tagName: "u",
    inheritable: true,
  };

  Trix.config.textAttributes.highlight = {
    tagName: "mark",
    inheritable: true,
  };

  Trix.config.blockAttributes.alignCenter = {
    tagName: "align-center",
    nestable: false,
    exclusive: true,
  };

  Trix.config.blockAttributes.alignLeft = {
    tagName: "align-left",
    nestable: false,
    exclusive: true,
  };

  Trix.config.blockAttributes.alignRight = {
    tagName: "align-right",
    nestable: false,
    exclusive: true,
  };
});

// Prevent Trix from accepting drag and drop file attachments
document.addEventListener("trix-file-accept", e => {
  e.preventDefault();
});

// Indent/de-indent support with undo.
// Trix's built-in Tab/Shift+Tab doesn't record undo entries and only
// operates on a single block. This handler intercepts all Tab events
// in the capture phase to add undo support and multi-block handling.
document.addEventListener(
  "keydown",
  event => {
    if (event.key !== "Tab") return;

    const trixEditor = event.target.closest("trix-editor");

    if (!trixEditor) return;

    const editor = trixEditor.editor;
    const selectedRange = editor.getSelectedRange();

    if (!selectedRange) return;

    const doc = editor.getDocument();
    const startLocation = doc.locationFromPosition(selectedRange[0]);
    const endLocation = doc.locationFromPosition(selectedRange[1]);

    // At block boundaries, the selection position can map to the edge of an
    // adjacent block. Clamp to only include blocks with actual selected content.
    let startIndex = startLocation.index;

    let endIndex = endLocation.index;
    const startBlock = doc.getBlockAtIndex(startIndex);

    if (
      startBlock &&
      startLocation.offset >= startBlock.getBlockBreakPosition() &&
      endLocation.index > startLocation.index
    ) {
      startIndex += 1;
    }

    if (endLocation.offset === 0 && endIndex > startIndex) {
      endIndex -= 1;
    }

    // For single-block, clamp the range to just that block
    if (startIndex >= endIndex) {
      endIndex = startIndex;
    }

    // Stop Trix's handler from also firing
    event.preventDefault();
    event.stopPropagation();

    editor.recordUndoEntry(event.shiftKey ? "Decrease Level" : "Increase Level");

    // Build a new blocks array with affected blocks modified by index.
    // For indent, check each block's predecessor to avoid creating nesting
    // gaps (which render as empty intermediate list items).
    // Build a mutable copy of the blocks array so each iteration can
    // reference the already-modified previous block.
    const blocks = doc.getBlocks();
    const newBlocks = Array.from(blocks);

    let changed = false;

    for (let i = startIndex; i <= endIndex; i++) {
      const block = newBlocks[i];

      if (event.shiftKey) {
        const newBlock = block.decreaseNestingLevel();

        if (newBlock !== block) {
          changed = true;
          newBlocks[i] = newBlock;
        }
      } else {
        // Only increase if the previous block (already modified) supports the
        // new nesting level. This prevents gaps that create empty intermediate
        // list items.
        const prevBlock = i > 0 ? newBlocks[i - 1] : null;

        if (prevBlock && prevBlock.getNestingLevel() >= block.getNestingLevel()) {
          const newBlock = block.increaseNestingLevel();

          if (newBlock) {
            changed = true;
            newBlocks[i] = newBlock;
          }
        }
      }
    }

    if (changed) {
      const newDoc = new doc.constructor(newBlocks);

      editor.composition.setDocument(newDoc);
    }
    editor.setSelectedRange(selectedRange);
  },
  true,
);

document.addEventListener("trix-action-invoke", event => {
  const { editor } = event.target;

  if (event.actionName === "x-reset") {
    editor.recordUndoEntry("Cleared Annotations");
    editor.setSelectedRange([0, editor.getDocument().getLength()]);
    editor.deactivateAttribute("strike");
    editor.setSelectedRange([0, editor.getDocument().getLength()]);
    editor.deactivateAttribute("highlight");
    editor.setSelectedRange([0, 0]);
  }
});
